import { Breakpoints } from '@angular/cdk/layout';
import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { InSubject } from '@atmi/core/utils';

import {BehaviorSubject, combineLatest, firstValueFrom, from, of} from 'rxjs';
import { filter, first, map } from 'rxjs/operators';

import { RoleService } from '@sim/role/services/role.service';
import {
  BaseTableFilteredComponent,
  makeQueryBuilder,
  makeQueryRules,
  TableContextMenu,
  TableField,
  TableSortType,
  UiTableComponent,
} from '@ui/ui.module';

import { CardsApiService, filePath, PersonApiService, SwalService } from '@atmi/core';
import { cardOA } from '@atmi/core/models';
import { CardDialogComponent } from '@atmi/shared/card-dialog/card-dialog.component';

@Component({
  selector: 'app-children-cards-manager',
  styleUrls: ['./children-cards-manager.component.scss'],
  templateUrl: './children-cards-manager.component.html',
})
export class ChildrenCardsManagerComponent
  extends BaseTableFilteredComponent<cardOA.CardOA, cardOA.CardOAFilter>
  implements UiTableComponent<any> {

  @Input() @InSubject() cardId?: string;
  private readonly cardIdChange = new BehaviorSubject<string | undefined>(undefined);

  @Input() @InSubject() card?: cardOA.CardOA;
  private readonly cardChange = new BehaviorSubject<cardOA.CardOA | undefined>(undefined);

  @Output() readonly childAdded = new EventEmitter();

  readonly fields: TableField<any>[] = [
    {
      context: (x) => x.previewUrl ? `${this.fileUrl}${x.previewUrl}` : undefined,
      header: this.translate.instant('Anteprima'),
      key: 'previewUrl',
      template: 'img',
    },
    {
      header: this.translate.instant('Codice unico'),
      key: 'codiceUnico',
      sortable: true,
    },
    {
      header: this.translate.instant('Num. Esemplare'),
      key: 'numeroEsemplare',
    },
    {
      header: this.translate.instant('Tipo figlia'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'tipoOperaFiglia',
      sortable: true,
    },
    {
      context: (x) => x.SGT.SGTT,
      header: this.translate.instant('Titolo soggetto'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'SGT.SGTT',
      sortable: true,
    },
    {
      header: this.translate.instant('Fornitore'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'fornitore$',
      template: 'async',
    },
    {
      align: 'right',
      header: '',
      key: 'context',
      showBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      template: 'context',
    },
  ];

  readonly pager$: any = combineLatest([
    this.cardChange.pipe(filter((card): card is cardOA.CardOA => !!card)),
    this.cardIdChange.pipe(filter((cardId): cardId is string => !!cardId)),
  ]).pipe(
    map(([_, cardId]) => (async (page?: number, limit?: number, sorting?: TableSortType) => {
      const rules = makeQueryRules({ benePadre: cardId }, cardOA.CardOAFiltersMeta);
      const query = makeQueryBuilder(rules);
      const sort: TableSortType = sorting && sorting.length ? sorting : [{ field: 'codiceUnico', type: 'asc' }];

      const resultPage = await firstValueFrom(this.cardsApi.indexOA({ page, limit, filter: query, sort }).pipe(first()));
      const data = resultPage.data.map((d) => ({
        ...d,
        fornitore$: d.fornitore
          ? this.personApi.get(d.fornitore).pipe(map((f) => f.ragioneSociale))
          : of(undefined),
      }));
      return {
        ...resultPage,
        data,
      };
    })),
  );

  readonly contextMenus: Array<TableContextMenu<cardOA.CardOA>> = [
    {
      action: (item) => this.router.navigate(['cards/oa/manager', item.id]),
      label: this.translate.instant('Visualizza'),
    },
  ];

  constructor(
    @Inject(filePath) private readonly fileUrl: string,
    private readonly cardsApi: CardsApiService,
    private readonly dialog: MatDialog,
    private readonly personApi: PersonApiService,
    private readonly roleService: RoleService,
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    protected readonly swalService: SwalService,
    protected readonly translate: TranslateService,
  ) {
    super(translate, router, route, cardOA.CardOAFiltersMeta, swalService, 'oa/manager', 'local');
    
    // Set default pagination to 50
    this.defaultLimit = 50;

    if (this.roleService.hasCapability('BeneOA', ['update'])) {
      this.contextMenus.push({
        action: (item) => item.id ? this.deleteHandler(item.id, item) : () => undefined,
        label: this.translate.instant('Rimuovi scheda figlia'),
      });
    }

  }

  deleteConcreteHandler(id: string) {
    return this.cardsApi.removeOA(id);
  }

  deleteSelectedConcreteHandler(ids: string[]) {
    const deleteApis = [new Promise(() => ids.map((id) => firstValueFrom(this.cardsApi.removeOA(id))))];

    return from(Promise.all(deleteApis));
  }

  async showAddNewChildrenHandler() {
    if (!this.card) {
      throw new Error('Cannot add a child card without the parent');
    }

    const subsetCard: Partial<cardOA.CardOA> = {
      AUT: this.card.AUT,
      DES: this.card.DES,
      DT: this.card.DT,
      ESC: this.card.ESC,
      MIS: this.card.MIS,
      MTC: this.card.MTC,
      OGT: this.card.OGT,
      SGT: this.card.SGT,
      TSK: this.card.TSK,
      benePadre: this.cardId,
      codiceUnico: this.card.codiceUnico,
    };

    const ref = this.dialog.open(CardDialogComponent, { data: { cardOa: subsetCard }, width: '784px' });
    const res = await firstValueFrom(ref.afterClosed().pipe(first()));

    if (res) {
      this.reload$.next(true);
      this.childAdded.emit();
    }
  }

}
